import React, { createContext, useContext, useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { hashWachtwoord, verifyWachtwoord, isGehasht } from '../utils/wachtwoord';
import {
  subscribeMedewerkers, subscribeVerlofaanvragen, subscribeOveruren,
  subscribeFeestdagen, subscribeJaararchief,
  subscribeFlexPlanning, subscribeFlexUren, subscribeFlexUitbetalingen,
  subscribeVerlofTypes, subscribeVakantiePerioden, subscribeRoosterWijzigingen,
  addVerlofaanvraag, updateVerlofaanvraag,
  addOveruren, updateOveruren,
  addMedewerker, updateMedewerker, deleteMedewerker, deleteMedewerkerCompleet, resetMedewerkerMutaties,
  addFeestdag, deleteFeestdag,
  addVakantiePeriode, updateVakantiePeriode, deleteVakantiePeriode,
  addRoosterWijziging, deleteRoosterWijziging,
  addJaararchief,
  addFlexPlanning, updateFlexPlanning, deleteFlexPlanning,
  addFlexUren, updateFlexUren,
  addFlexUitbetaling,
  addVerlofType, updateVerlofType, deleteVerlofType,
  subscribeMenuConfig, saveMenuConfig,
} from '../services/dataService';
import sharepointConfig from '../config/sharepointConfig';
import {
  isConnected as isOutlookConnected, autoSyncWerkrooster,
  autoSyncVerlofEvent, autoSyncOverurenEvent, autoSyncFlexPlanningEvent,
  deleteCalendarEvent, createCalendarEvent, createOverurenEvent, createFlexPlanningEvent,
} from '../services/calendarService';
import {
  subscribeNotificaties, maakNotificatie, markeerGelezen as markeerGelezenService,
  markeerAllesGelezen as markeerAllesGelezenService, vraagPushPermissie, slaTokenOp,
  verwijderOudeNotificaties, registreerPushServiceWorker, toonLokaleNotificatie,
} from '../services/notificatieService';
import { db } from '../config/firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';

const VERLOF_FACTOR = 5; // weekuren × 5 = jaarverlofuren

const AppContext = createContext(null);

export function AppProvider({ children }) {
  const [currentUserRaw, setCurrentUserRaw] = useState(null);
  const [medewerkersRaw, setMedewerkersRaw] = useState([]);
  const [verlofaanvragenRaw, setVerlofaanvragenRaw] = useState([]);
  const [overurenRaw, setOverurenRaw] = useState([]);
  const [feestdagenRaw, setFeestdagenRaw] = useState([]);
  const [jaararchief, setJaararchief] = useState([]);
  const [flexPlanningRaw, setFlexPlanningRaw] = useState([]);
  const [flexUrenRaw, setFlexUrenRaw] = useState([]);
  const [flexUitbetalingenRaw, setFlexUitbetalingenRaw] = useState([]);
  const [verlofTypesRaw, setVerlofTypesRaw] = useState([]);
  const [vakantiePeriodenRaw, setVakantiePeriodenRaw] = useState([]);
  const [roosterWijzigingenRaw, setRoosterWijzigingenRaw] = useState([]);
  const [menuConfigRaw, setMenuConfigRaw] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notificatiesRaw, setNotificatiesRaw] = useState([]);

  // Subscribe to all Firestore collections
  useEffect(() => {
    let loadCount = 0;
    const needed = 12;
    const checkLoaded = () => { loadCount++; if (loadCount >= needed) setLoading(false); };

    const unsubs = [
      subscribeMedewerkers((data) => { setMedewerkersRaw(data); checkLoaded(); }),
      subscribeVerlofaanvragen((data) => { setVerlofaanvragenRaw(data); checkLoaded(); }),
      subscribeOveruren((data) => { setOverurenRaw(data); checkLoaded(); }),
      subscribeFeestdagen((data) => { setFeestdagenRaw(data); checkLoaded(); }),
      subscribeJaararchief((data) => { setJaararchief(data); checkLoaded(); }),
      subscribeFlexPlanning((data) => { setFlexPlanningRaw(data); checkLoaded(); }),
      subscribeFlexUren((data) => { setFlexUrenRaw(data); checkLoaded(); }),
      subscribeFlexUitbetalingen((data) => { setFlexUitbetalingenRaw(data); checkLoaded(); }),
      subscribeVerlofTypes((data) => { setVerlofTypesRaw(data); checkLoaded(); }),
      subscribeVakantiePerioden((data) => { setVakantiePeriodenRaw(data); checkLoaded(); }),
      subscribeRoosterWijzigingen((data) => { setRoosterWijzigingenRaw(data); checkLoaded(); }),
      subscribeMenuConfig((data) => { setMenuConfigRaw(data); checkLoaded(); }),
    ];

    return () => unsubs.forEach(fn => fn());
  }, []);

  // Seed default verlofTypes if Firestore collection is empty
  useEffect(() => {
    if (loading) return;
    if (verlofTypesRaw.length === 0) {
      const defaults = sharepointConfig.verlofTypes.map((t, i) => ({
        ...t, trektAfVanVerlof: t.aftrekSaldo === 'verlof', aftrekSaldo: t.aftrekSaldo || 'verlof', sortOrder: i,
      }));
      defaults.forEach(t => addVerlofType(t));
    } else {
      // Sync: voeg ontbrekende standaard verlofTypes toe (eenmalig)
      const bestaandeKeys = verlofTypesRaw.map(t => t.key);
      const ontbrekend = sharepointConfig.verlofTypes.filter(t => !bestaandeKeys.includes(t.key));
      if (ontbrekend.length > 0) {
        const syncKey = 'slaaploods_vtype_sync_' + ontbrekend.map(t => t.key).join('_');
        if (!localStorage.getItem(syncKey)) {
          localStorage.setItem(syncKey, '1');
          ontbrekend.forEach((t, i) => {
            addVerlofType({
              ...t, trektAfVanVerlof: t.aftrekSaldo === 'verlof',
              aftrekSaldo: t.aftrekSaldo || 'verlof',
              sortOrder: verlofTypesRaw.length + i,
            });
          });
        }
      }

      // Migration: fix bijzonder verlof aftrekSaldo and clean duplicates
      if (!localStorage.getItem('slaaploods_vtype_fix_v3')) {
        localStorage.setItem('slaaploods_vtype_fix_v3', '1');
        // Fix bijzonder verlof: should be 'geen' aftrek
        const bv = verlofTypesRaw.find(t => t.key === 'bijzonder' && t.aftrekSaldo === 'verlof');
        if (bv) updateVerlofType(bv.id, { aftrekSaldo: 'geen', trektAfVanVerlof: false });
        // Remove duplicate verlofTypes (keep first by sortOrder)
        const seen = new Set();
        for (const t of [...verlofTypesRaw].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))) {
          if (seen.has(t.key)) deleteVerlofType(t.id);
          else seen.add(t.key);
        }
      }

      // Migration v4: rename vakantie→Vrij, sync beschrijvingen + aftrekInfo
      if (!localStorage.getItem('slaaploods_vtype_fix_v4')) {
        localStorage.setItem('slaaploods_vtype_fix_v4', '1');
        for (const raw of verlofTypesRaw) {
          const cfgType = sharepointConfig.verlofTypes.find(c => c.key === raw.key);
          if (cfgType) {
            const updates = {};
            if (cfgType.text && cfgType.text !== raw.text) updates.text = cfgType.text;
            if (cfgType.beschrijving && !raw.beschrijving) updates.beschrijving = cfgType.beschrijving;
            if (cfgType.aftrekInfo && !raw.aftrekInfo) updates.aftrekInfo = cfgType.aftrekInfo;
            if (Object.keys(updates).length > 0) updateVerlofType(raw.id, updates);
          }
        }
      }

      // Migration v5: force-sync beschrijving + aftrekInfo from config (overschrijft bestaande waarden)
      if (!localStorage.getItem('slaaploods_vtype_fix_v5')) {
        localStorage.setItem('slaaploods_vtype_fix_v5', '1');
        for (const raw of verlofTypesRaw) {
          const cfgType = sharepointConfig.verlofTypes.find(c => c.key === raw.key);
          if (cfgType) {
            const updates = {};
            if (cfgType.beschrijving !== undefined && cfgType.beschrijving !== raw.beschrijving)
              updates.beschrijving = cfgType.beschrijving;
            if (cfgType.aftrekInfo !== undefined && cfgType.aftrekInfo !== raw.aftrekInfo)
              updates.aftrekInfo = cfgType.aftrekInfo;
            if (cfgType.text !== undefined && cfgType.text !== raw.text)
              updates.text = cfgType.text;
            if (Object.keys(updates).length > 0) updateVerlofType(raw.id, updates);
          }
        }
      }
    }
  }, [loading, verlofTypesRaw.length]);

  // One-time migration: old role system → new multi-role system + create Willem
  useEffect(() => {
    if (loading || medewerkersRaw.length === 0) return;
    const migrationDone = localStorage.getItem('slaaploods_role_migration_v2');
    if (migrationDone) return;

    (async () => {
      // Migrate old admin/manager roles
      for (const m of medewerkersRaw) {
        if (m.rol === 'admin' && m.isAdmin === undefined) {
          await updateMedewerker(m.id, { role: 'medewerker', isAdmin: true, isManager: true, status: 'actief' });
        } else if (m.rol === 'manager' && m.isManager === undefined) {
          await updateMedewerker(m.id, { role: 'medewerker', isManager: true, status: 'actief' });
        }
      }

      // Ensure Willem beheerder account exists
      const willemExists = medewerkersRaw.find(m => m.email === 'willem@slaaploods.nl');
      if (!willemExists) {
        await addMedewerker({
          naam: 'Willem', email: 'willem@slaaploods.nl', wachtwoord: 'admin123',
          rol: 'medewerker', isAdmin: true, isManager: true, status: 'actief',
          afdeling: 'Beheer', contractUren: 40,
          indienstDatum: new Date().toISOString().split('T')[0],
          contractWijzigingen: [], meegenomenUren: 0, meegenomenUrenJaar: null,
          flexEnabled: false, werkrooster: null, telefoon: '',
        });
      } else if (!willemExists.isAdmin) {
        // Ensure Willem has admin
        await updateMedewerker(willemExists.id, { isAdmin: true, isManager: true });
      }

      localStorage.setItem('slaaploods_role_migration_v2', '1');
    })();
  }, [loading, medewerkersRaw]);

  // Sorted verlofTypes (from Firestore, fallback to config)
  const verlofTypes = useMemo(() => {
    const raw = verlofTypesRaw.length > 0
      ? verlofTypesRaw.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
      : sharepointConfig.verlofTypes.map(t => ({ ...t, trektAfVanVerlof: t.key !== 'ziek', aftrekSaldo: t.aftrekSaldo || 'verlof' }));
    // Deduplicate by key (keep first occurrence)
    const seen = new Set();
    return raw.filter(t => { if (seen.has(t.key)) return false; seen.add(t.key); return true; });
  }, [verlofTypesRaw]);

  // Helper: check if a leave type deducts from balance
  const trektAfVanVerlof = (type) => {
    const t = verlofTypes.find(vt => vt.key === type);
    if (!t) return true;
    // aftrekSaldo: 'verlof' = trekt af van verlof, 'flex' = trekt af van flex, 'flex-plus' = voegt toe aan flex, 'geen' = geen aftrek
    if (t.aftrekSaldo === 'flex' || t.aftrekSaldo === 'flex-plus' || t.aftrekSaldo === 'geen') return false;
    return t.trektAfVanVerlof !== false;
  };

  // Restore session
  useEffect(() => {
    try {
      const saved = localStorage.getItem('slaaploods_session');
      if (saved) setCurrentUserRaw(JSON.parse(saved));
    } catch (e) { /* ignore */ }
  }, []);

  // Keep currentUser in sync with Firestore
  useEffect(() => {
    if (!currentUserRaw || medewerkersRaw.length === 0) return;
    const fresh = medewerkersRaw.find(m => m.id === currentUserRaw.id);
    if (fresh) {
      // Auto-logout if account was deactivated
      if (fresh.status === 'inactief' || fresh.status === 'uitdienst') {
        setCurrentUserRaw(null);
        localStorage.removeItem('slaaploods_session');
        localStorage.removeItem('slaaploods_activeRol');
        return;
      }
      // Altijd Firestore-waarde voor mustChangePassword gebruiken (nooit localStorage stale waarde bewaren)
      const merged = { ...fresh };
      if (JSON.stringify(merged) !== JSON.stringify(currentUserRaw)) {
        setCurrentUserRaw(merged);
        localStorage.setItem('slaaploods_session', JSON.stringify(merged));
      }
    }
  }, [medewerkersRaw, currentUserRaw]);

  // Subscribe to notifications for current user
  useEffect(() => {
    if (!currentUserRaw?.id) { setNotificatiesRaw([]); return; }
    // IDs die al bestonden bij het STARTEN van deze sessie — worden nooit als "nieuw" gezien
    // Dit voorkomt dat bij inloggen op nieuw device alle ongelezen notificaties als nieuw tonen
    let sessieStartIds = null;
    const unsub = subscribeNotificaties(currentUserRaw.id, (notifs) => {
      setNotificatiesRaw(prev => {
        // Eerste snapshot: sla alle huidige IDs op als "sessie-start" set
        if (sessieStartIds === null) {
          sessieStartIds = new Set(notifs.map(n => n.id));
          return notifs;
        }
        // Detect ALLEEN notificaties die na sessie-start zijn aangemaakt
        const prevIds = new Set(prev.map(n => n.id));
        const nieuweNotifs = notifs.filter(n =>
          !prevIds.has(n.id) &&          // niet in vorige state
          !sessieStartIds.has(n.id) &&   // niet al aanwezig bij inloggen
          !n.gelezen                      // nog ongelezen
        );
        nieuweNotifs.forEach(n => {
          toonLokaleNotificatie(n.titel || 'SL Verlof', n.bericht || 'Nieuwe melding', n.link);
        });
        return notifs;
      });
    });
    // Clean old notifications once
    verwijderOudeNotificaties(currentUserRaw.id).catch(() => {});
    // Register push service worker and request permission (non-blocking)
    registreerPushServiceWorker().catch(() => {});
    // Get VAPID key from Firestore settings, then request push
    (async () => {
      try {
        const settingsSnap = await getDocs(query(collection(db, 'settings'), where('key', '==', 'vapidKey')));
        const vapidKey = settingsSnap.empty ? '' : settingsSnap.docs[0].data().value || '';
        const token = await vraagPushPermissie(vapidKey);
        if (token && token !== 'local-only') await slaTokenOp(currentUserRaw.id, token);
      } catch (e) { console.warn('Push setup:', e.message); }
    })();
    // Auto-herstel Outlook verbinding (silent — werkt als MSAL token nog geldig is in localStorage)
    // Slaat resultaat op in localStorage zodat andere components dit kunnen lezen
    (async () => {
      try {
        const { getOutlookStatus: getStatus } = await import('../services/calendarService');
        const status = await getStatus();
        try { localStorage.setItem('slaaploods_outlook_connected', status.connected ? '1' : '0'); } catch {}
      } catch {}
    })();
    return unsub;
  }, [currentUserRaw?.id]);

  const notificaties = notificatiesRaw;
  const ongelezen = notificatiesRaw.filter(n => !n.gelezen).length;

  // PWA App Badge — toont getal op app-icoon
  useEffect(() => {
    if ('setAppBadge' in navigator) {
      if (ongelezen > 0) navigator.setAppBadge(ongelezen).catch(() => {});
      else navigator.clearAppBadge().catch(() => {});
    }
  }, [ongelezen]);

  const markeerGelezenFn = (id) => markeerGelezenService(id);
  const markeerAllesGelezenFn = () => { if (currentUserRaw?.id) markeerAllesGelezenService(currentUserRaw.id); };

  // Role system: users can have multiple roles (medewerker + manager + admin)
  // activeRol determines which "profile" they're currently using
  const [activeRol, setActiveRol] = useState(() => {
    try { return localStorage.getItem('slaaploods_activeRol') || null; } catch { return null; }
  });

  // Compute available roles for current user
  const beschikbareRollen = useMemo(() => {
    if (!currentUserRaw) return [];
    const rollen = [];
    if (currentUserRaw.rol === 'flexkracht') {
      rollen.push('flexkracht');
    } else {
      rollen.push('medewerker');
      // Medewerker met flexkracht profiel → kan ook naar flexkracht view switchen
      if (currentUserRaw.heeftFlexProfiel) rollen.push('flexkracht');
    }
    if (currentUserRaw.isManager) rollen.push('manager');
    if (currentUserRaw.isAdmin) rollen.push('admin');
    return rollen;
  }, [currentUserRaw]);

  // Effective active role (default to first available)
  const effectiefRol = useMemo(() => {
    if (!currentUserRaw) return null;
    // Admin impersonation: trust activeRol even if not in own beschikbareRollen
    // (bijv. 'flexkracht' staat niet in admin's rollen maar is nodig bij viewAs)
    if (currentUserRaw.isAdmin && activeRol) return activeRol;
    if (activeRol && beschikbareRollen.includes(activeRol)) return activeRol;
    // Default: highest available role
    if (beschikbareRollen.includes('admin')) return 'admin';
    if (beschikbareRollen.includes('manager')) return 'manager';
    return beschikbareRollen[0] || 'medewerker';
  }, [currentUserRaw, activeRol, beschikbareRollen]);

  const switchRol = (newRol) => {
    if (beschikbareRollen.includes(newRol)) {
      setActiveRol(newRol);
      localStorage.setItem('slaaploods_activeRol', newRol);
      setViewAsUserId(null); // stop impersonation on role switch
    }
  };

  // ---- "Inloggen als" (admin impersonation) ----
  const [viewAsUserId, setViewAsUserId] = useState(null);

  const startViewAs = (userId) => {
    if (!currentUserRaw?.isAdmin) return;
    setViewAsUserId(userId || null);
    if (userId) {
      // Gebruik de ROL van de geïmpersoneerde gebruiker (niet hardcoded 'medewerker')
      const targetUser = medewerkersRaw.find(m => m.id === userId);
      const targetRol = targetUser?.rol === 'flexkracht' ? 'flexkracht' : 'medewerker';
      setActiveRol(targetRol);
      localStorage.setItem('slaaploods_activeRol', targetRol);
    }
  };
  const stopViewAs = () => setViewAsUserId(null);

  // Role helpers based on active profile
  const isAdmin = effectiefRol === 'admin';
  const isManager = effectiefRol === 'manager' || effectiefRol === 'admin';
  const isFlexkracht = effectiefRol === 'flexkracht';
  const isMedewerker = effectiefRol === 'medewerker';
  const canManage = isAdmin || isManager;
  const currentYear = new Date().getFullYear();

  // ---- Profiel labels ----
  const rolNamen = { admin: 'Beheerdersprofiel', manager: 'Managersprofiel', medewerker: 'Medewerkersprofiel', flexkracht: 'Flexkrachtprofiel' };
  const rolNamenKort = { admin: 'Beheerder', manager: 'Manager', medewerker: 'Medewerker', flexkracht: 'Flexkracht' };

  // Helper: stuur in-app notificatie
  const stuurNotificatie = async ({ ontvangerIds, type, titel, bericht, link }) => {
    try {
      await maakNotificatie({
        ontvangerIds, type, titel, bericht, link,
        afzenderId: currentUserRaw?.id,
        afzenderNaam: `${currentUserRaw?.naam?.split(' ')[0]} (${rolNamenKort[effectiefRol] || effectiefRol})`,
      });
    } catch (e) { console.warn('Notificatie fout:', e.message); }
  };

  // Helper: vind admin/manager IDs voor notificaties
  const getAdminManagerIds = () =>
    medewerkersRaw.filter(m => m.isAdmin || m.isManager).map(m => m.id).filter(id => id !== currentUserRaw?.id);

  // Pro-rata verlofuren berekening
  const berekenVerlofUrenProRata = (contractUrenWeek, indienstDatum, contractWijzigingen, jaar) => {
    const jaarStart = new Date(jaar, 0, 1);
    const jaarEind = new Date(jaar, 11, 31);
    const dagenInJaar = (jaarEind - jaarStart) / (1000 * 60 * 60 * 24) + 1;
    const verlofUrenJaar = (contractUrenWeek || 0) * VERLOF_FACTOR;

    // Bepaal startdatum voor dit jaar
    let effectieveStart = jaarStart;
    if (indienstDatum) {
      const indienst = new Date(indienstDatum);
      if (indienst > jaarEind) return 0; // nog niet in dienst dit jaar
      if (indienst > jaarStart) effectieveStart = indienst;
    }

    // Verzamel alle contractperiodes in dit jaar
    const wijzigingen = (contractWijzigingen || [])
      .filter(w => w.ingangsDatum && w.contractUren !== undefined)
      .sort((a, b) => a.ingangsDatum.localeCompare(b.ingangsDatum));

    // Bouw periodes op: [{start, eind, verlofUren}]
    const periodes = [];
    let huidigeVerlof = verlofUrenJaar;
    let huidigStart = effectieveStart;

    for (const w of wijzigingen) {
      const wijzigDatum = new Date(w.ingangsDatum);
      if (wijzigDatum <= effectieveStart) {
        // Wijziging vóór effectieve start: gebruik nieuwe uren als basis
        huidigeVerlof = (w.contractUren || 0) * VERLOF_FACTOR;
        continue;
      }
      if (wijzigDatum > jaarEind) break;

      // Periode tot wijziging
      const periodeEind = new Date(wijzigDatum);
      periodeEind.setDate(periodeEind.getDate() - 1);
      if (periodeEind >= huidigStart) {
        periodes.push({ start: huidigStart, eind: periodeEind, uren: huidigeVerlof });
      }
      huidigStart = wijzigDatum;
      huidigeVerlof = (w.contractUren || 0) * VERLOF_FACTOR;
    }

    // Laatste periode tot einde jaar
    periodes.push({ start: huidigStart, eind: jaarEind, uren: huidigeVerlof });

    // Bereken totaal pro-rata
    let totaal = 0;
    for (const p of periodes) {
      const dagen = (p.eind - p.start) / (1000 * 60 * 60 * 24) + 1;
      totaal += p.uren * (dagen / dagenInJaar);
    }

    return Math.round(totaal * 10) / 10; // 1 decimaal
  };

  // ViewAs user (admin impersonation) — needed BEFORE saldo for correct impersonation
  const viewAsUserRaw = useMemo(() => {
    if (!viewAsUserId || !currentUserRaw?.isAdmin) return null;
    return medewerkersRaw.find(m => m.id === viewAsUserId) || null;
  }, [viewAsUserId, medewerkersRaw, currentUserRaw]);

  // Effective user: the viewed-as user or the actual logged-in user
  const effectiveUser = viewAsUserRaw || currentUserRaw;

  // Helper: get ISO week start (Monday) for a date
  const getWeekStart = (date) => {
    const d = new Date(date);
    const day = d.getDay() || 7;
    d.setDate(d.getDate() - day + 1);
    d.setHours(0, 0, 0, 0);
    return d;
  };

  // Helper: get month start for a date
  const getMonthStart = (date) => {
    const d = new Date(date);
    d.setDate(1);
    d.setHours(0, 0, 0, 0);
    return d;
  };

  // Saldo for effective user (respects viewAs impersonation)
  const saldo = useMemo(() => {
    if (!effectiveUser) return null;
    const userId = effectiveUser.id;
    const contractUren = effectiveUser.contractUren || 0;
    const meegenomenUren = effectiveUser.meegenomenUren || 0;
    const weekFlexUren = effectiveUser.weekFlexUren || 0;
    const berekendeUren = berekenVerlofUrenProRata(
      contractUren, effectiveUser.indienstDatum,
      effectiveUser.contractWijzigingen, currentYear
    );
    const totaalUren = berekendeUren + meegenomenUren;

    // Verlof opgenomen (alleen types die aftrekken van verlof, niet van flex)
    const opgenomenVerlof = verlofaanvragenRaw
      .filter(v =>
        v.medewerkerId === userId &&
        v.status === 'goedgekeurd' &&
        v.jaar === currentYear &&
        trektAfVanVerlof(v.type)
      )
      .reduce((sum, v) => {
        const overurenDeel = v.overurenGebruikt || 0;
        const verlofType = verlofTypes.find(t => t.key === v.type);
        if (verlofType && (verlofType.aftrekSaldo === 'flex' || verlofType.aftrekSaldo === 'flex-plus')) return sum;
        return sum + Math.max(0, (v.uren || 0) - overurenDeel);
      }, 0);

    // Overuren saldo
    const todayStr = new Date().toISOString().split('T')[0];
    const mijnOveruren = overurenRaw.filter(o => o.medewerkerId === userId);

    // Zaterdag onbetaald drempel
    const zaterdagDrempel = effectiveUser.zaterdagDrempel || 0;
    const isZaterdag = (datum) => {
      if (!datum) return false;
      return new Date(datum + 'T12:00:00').getDay() === 6;
    };

    // Alle goedgekeurde overuren t/m vandaag, GESCHEIDEN in zaterdag vs doordeweeks
    const overurenGoedgekeurdTotVandaag = mijnOveruren.filter(o => o.status === 'goedgekeurd' && (o.datum || '') <= todayStr);
    const zaterdagUrenGemaakt = overurenGoedgekeurdTotVandaag
      .filter(o => isZaterdag(o.datum))
      .reduce((sum, o) => sum + (o.uren || 0), 0);
    const doordeweeksUren = overurenGoedgekeurdTotVandaag
      .filter(o => !isZaterdag(o.datum))
      .reduce((sum, o) => sum + (o.uren || 0), 0);

    // Toekomstige zaterdaguren (goedgekeurd of ingediend, datum > vandaag)
    const zaterdagUrenGepland = mijnOveruren
      .filter(o => (o.status === 'goedgekeurd' || o.status === 'ingediend') && (o.datum || '') > todayStr && isZaterdag(o.datum))
      .reduce((sum, o) => sum + (o.uren || 0), 0);

    // Totaal = gemaakt + gepland (voor voortgangsbalk)
    const zaterdagUrenTotaal = zaterdagUrenGemaakt + zaterdagUrenGepland;

    // Zaterdag uren: eerste {drempel} uren zijn onbetaald, daarna tellen ze als overuren
    // Alleen gemaakte uren tellen mee voor overurenSaldo
    const zaterdagOnbetaald = zaterdagDrempel > 0 ? Math.min(zaterdagUrenGemaakt, zaterdagDrempel) : 0;
    const zaterdagAlsOveruren = zaterdagDrempel > 0 ? Math.max(0, zaterdagUrenGemaakt - zaterdagDrempel) : zaterdagUrenGemaakt;

    const overurenVerdiend = doordeweeksUren + zaterdagAlsOveruren;

    const overurenToekomstig = mijnOveruren
      .filter(o => (o.status === 'goedgekeurd' || o.status === 'ingediend') && (o.datum || '') > todayStr)
      .reduce((sum, o) => sum + (o.uren || 0), 0);

    const overurenUitbetaald = mijnOveruren
      .filter(o => o.status === 'uitbetaald')
      .reduce((sum, o) => sum + (o.uren || 0), 0);

    const overurenGebruiktTotaal = verlofaanvragenRaw
      .filter(v => v.medewerkerId === userId && v.status === 'goedgekeurd' && v.jaar === currentYear)
      .reduce((sum, v) => sum + (v.overurenGebruikt || 0), 0);

    const overurenSaldo = overurenVerdiend - overurenGebruiktTotaal - overurenUitbetaald;

    // ---- Flex/ADV balance ----
    // Helper: is dit een flex-plus boeking (verhoogt saldo)?
    const isFlexPlus = (v) => {
      if (v.type === 'flex-plus') return true;
      const vt = verlofTypes.find(t => t.key === v.type);
      return vt?.aftrekSaldo === 'flex-plus';
    };
    // Helper: is dit een flex-min boeking (verlaagt saldo)?
    const isFlexMin = (v) => {
      const vt = verlofTypes.find(t => t.key === v.type);
      return vt?.aftrekSaldo === 'flex';
    };

    // All flex bookings this year (plus én min)
    const flexBoekingen = verlofaanvragenRaw.filter(v =>
      v.medewerkerId === userId && v.status === 'goedgekeurd' && v.jaar === currentYear &&
      (isFlexPlus(v) || isFlexMin(v))
    );

    const flexPlusUren = flexBoekingen
      .filter(v => isFlexPlus(v))
      .reduce((sum, v) => sum + (v.uren || 0), 0);

    const flexMinUren = flexBoekingen
      .filter(v => isFlexMin(v))
      .reduce((sum, v) => sum + (v.uren || 0), 0);

    const flexBalans = flexPlusUren - flexMinUren;

    // Per-week flex-min totals (for validation in forms)
    const now = new Date();
    const thisWeekStart = getWeekStart(now);
    const thisMonthStart = getMonthStart(now);
    const thisMonthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);

    // Flex-min used this ISO week
    const flexMinDezeWeek = flexBoekingen
      .filter(v => {
        if (!isFlexMin(v)) return false;
        const d = new Date(v.startDatum || v.StartDatum);
        const ws = getWeekStart(d);
        return ws.getTime() === thisWeekStart.getTime();
      })
      .reduce((sum, v) => sum + (v.uren || 0), 0);

    // Flex this calendar month (plus and min separately)
    const flexPlusDezeMaand = flexBoekingen
      .filter(v => {
        if (!isFlexPlus(v)) return false;
        const d = new Date(v.startDatum || v.StartDatum);
        return d >= thisMonthStart && d <= thisMonthEnd;
      })
      .reduce((sum, v) => sum + (v.uren || 0), 0);

    const flexMinDezeMaand = flexBoekingen
      .filter(v => {
        if (!isFlexMin(v)) return false;
        const d = new Date(v.startDatum || v.StartDatum);
        return d >= thisMonthStart && d <= thisMonthEnd;
      })
      .reduce((sum, v) => sum + (v.uren || 0), 0);

    const flexBalansDezeMaand = flexPlusDezeMaand - flexMinDezeMaand;

    // Max flex-min per week = 8u (hard cap)
    const FLEX_MIN_WEEK_MAX = 8;
    // Max flex-min per maand = weekFlexUren × ~4 weken (maandelijks moet balans naar 0)
    const flexMinMaandMax = weekFlexUren * 4;

    return {
      contractUren,
      berekendeUren,
      meegenomenUren,
      totaalUren,
      opgenomenUren: opgenomenVerlof,
      resterend: totaalUren - opgenomenVerlof,
      overurenSaldo,
      overurenVerdiend,
      overurenGebruiktTotaal,
      overurenUitbetaald,
      overurenToekomstig,
      zaterdagDrempel,
      zaterdagUrenTotaal,
      zaterdagUrenGemaakt,
      zaterdagUrenGepland,
      zaterdagOnbetaald,
      zaterdagAlsOveruren,
      weekFlexUren,
      flexPlusUren,
      flexMinUren,
      flexBalans,
      flexMinDezeWeek,
      flexPlusDezeMaand,
      flexMinDezeMaand,
      flexBalansDezeMaand,
      flexMinWeekMax: FLEX_MIN_WEEK_MAX,
      flexMinMaandMax,
      flexMinWeekBeschikbaar: Math.max(0, FLEX_MIN_WEEK_MAX - flexMinDezeWeek),
      flexMinMaandBeschikbaar: Math.max(0, flexMinMaandMax - flexMinDezeMaand),
    };
  }, [effectiveUser, viewAsUserRaw, verlofaanvragenRaw, overurenRaw, verlofTypes, currentYear]);

  // Flex saldo for flexkracht
  const flexSaldo = useMemo(() => {
    if (!effectiveUser) return null;
    const targetId = effectiveUser.id;
    const ingepland = flexPlanningRaw
      .filter(p => p.flexkrachtId === targetId && new Date(p.datum).getFullYear() === currentYear)
      .reduce((sum, p) => sum + (p.uren || 0), 0);
    const gewerkt = flexUrenRaw
      .filter(u => u.flexkrachtId === targetId && new Date(u.datum).getFullYear() === currentYear)
      .reduce((sum, u) => sum + (u.uren || 0), 0);
    const uitbetaald = flexUitbetalingenRaw
      .filter(u => u.flexkrachtId === targetId && new Date(u.datum).getFullYear() === currentYear)
      .reduce((sum, u) => sum + (u.uren || 0), 0);

    return { ingepland, gewerkt, uitbetaald, openstaand: gewerkt - uitbetaald };
  }, [effectiveUser, flexPlanningRaw, flexUrenRaw, flexUitbetalingenRaw, currentYear]);

  // PascalCase mapping for components
  // Real user = always the actual logged-in person (for auth, logging, ingediendDoor)
  const realUser = useMemo(() => {
    if (!currentUserRaw) return null;
    return {
      ...currentUserRaw,
      Naam: currentUserRaw.naam,
      Email: currentUserRaw.email,
      Id: currentUserRaw.id,
      id: currentUserRaw.id,
      Rol: effectiefRol || currentUserRaw.rol,
      IsAdmin: currentUserRaw.isAdmin ?? false,
      IsManager: currentUserRaw.isManager ?? false,
    };
  }, [currentUserRaw, effectiefRol]);

  const mappedCurrentUser = useMemo(() => {
    const source = effectiveUser;
    if (!source) return null;
    return {
      ...source,
      Naam: source.naam,
      Email: source.email,
      Afdeling: source.afdeling,
      Rol: viewAsUserRaw ? source.rol : (effectiefRol || source.rol),
      IsManager: source.isManager ?? false,
      IsAdmin: source.isAdmin ?? false,
      Status: source.status || 'actief',
      FlexEnabled: source.flexEnabled ?? false,
      Werkrooster: source.werkrooster || null,
      Telefoon: source.telefoon || '',
      HeeftFlexProfiel: source.heeftFlexProfiel ?? false,
      ShowroomMedewerker: source.showroomMedewerker ?? false,
      ZaterdagDrempel: source.zaterdagDrempel ?? 0,
      VerkorteNaam: source.verkorteNaam || '',
      MustChangePassword: source.mustChangePassword === true,
    };
  }, [currentUserRaw, viewAsUserRaw, effectiefRol]);

  // Profiel label: "Beheerdersprofiel: Willem" / "Medewerkersprofiel: Erwin"
  const profielLabel = useMemo(() => {
    if (!currentUserRaw) return '';
    const naam = viewAsUserRaw ? viewAsUserRaw.naam : currentUserRaw.naam;
    const prefix = rolNamen[effectiefRol] || 'Profiel';
    return `${prefix}: ${naam}`;
  }, [currentUserRaw, viewAsUserRaw, effectiefRol]);

  const medewerkers = useMemo(() =>
    medewerkersRaw.map(m => {
      // Flexkrachten hebben geen verlofuren
      const isFlexkrachtAccount = m.rol === 'flexkracht';
      const berekendeUren = isFlexkrachtAccount ? 0 : berekenVerlofUrenProRata(
        m.contractUren || 0, m.indienstDatum, m.contractWijzigingen, currentYear
      );
      const opgenomen = isFlexkrachtAccount ? 0 : verlofaanvragenRaw
        .filter(v => v.medewerkerId === m.id && v.status === 'goedgekeurd' && v.jaar === currentYear && trektAfVanVerlof(v.type))
        .reduce((sum, v) => {
          const vt = verlofTypes.find(t => t.key === v.type);
          if (vt && vt.aftrekSaldo === 'flex') return sum;
          return sum + Math.max(0, (v.uren || 0) - (v.overurenGebruikt || 0));
        }, 0);
      return {
        ...m,
        Id: m.id,
        Naam: m.naam,
        Email: m.email,
        Afdeling: m.afdeling,
        Rol: m.rol,
        IsManager: m.isManager ?? false,
        IsAdmin: m.isAdmin ?? false,
        Status: m.status || 'actief',
        UitDienstDatum: m.uitDienstDatum || null,
        ContractUren: m.contractUren || 0,
        BerekendeVerlofUren: berekendeUren,
        MeegenomenUren: m.meegenomenUren || 0,
        IndienstDatum: m.indienstDatum || null,
        ContractWijzigingen: m.contractWijzigingen || [],
        FlexEnabled: m.flexEnabled ?? false,
        WeekFlexUren: m.weekFlexUren ?? 0,
        Werkrooster: m.werkrooster || null,
        Telefoon: m.telefoon || '',
        HeeftFlexProfiel: m.heeftFlexProfiel ?? false,
        ShowroomMedewerker: m.showroomMedewerker ?? false,
        ZaterdagDrempel: m.zaterdagDrempel ?? 0,
        VerkorteNaam: m.verkorteNaam || '',
        Kleur: m.kleur || '',
        VerlofOpgenomen: opgenomen,
        pendingApproval: m.pendingApproval ?? false,
      };
    }),
    [medewerkersRaw, verlofaanvragenRaw, currentYear]
  );

  // Kleur mapping per medewerker — zelfde logica als Calendar.js
  const medewerkerKleurMap = useMemo(() => {
    const map = {};
    const kleuren = sharepointConfig.medewerkerKleuren;
    const sorted = [...medewerkers].filter(m => m.Rol !== 'flexkracht').sort((a, b) => a.Naam.localeCompare(b.Naam));
    sorted.forEach((m, i) => { map[m.Id] = m.Kleur || kleuren[i % kleuren.length]; });
    medewerkers.filter(m => m.Rol === 'flexkracht').forEach(m => { map[m.Id] = m.Kleur || '#E86E1C'; });
    return map;
  }, [medewerkers]);

  const verlofaanvragen = useMemo(() =>
    verlofaanvragenRaw.map(v => ({
      ...v,
      Id: v.id, Status: v.status,
      MedewerkerNaam: v.medewerkerNaam,
      IngediendDoorNaam: v.ingediendDoorNaam || '',
      Type: v.type,
      StartDatum: v.startDatum, EindDatum: v.eindDatum,
      StartTijd: v.beginTijd || null, EindTijd: v.eindTijd || null,
      Uren: v.uren, Reden: v.reden,
      isOpenZiek: v.isOpenZiek ?? false,
      gebruikOveruren: v.gebruikOveruren ?? false,
      overurenGebruikt: v.overurenGebruikt ?? 0,
      calendarEventId: v.calendarEventId || null,
      WijzerResultaat: v.wijzerResultaat || '',
      WijzerWettelijk: v.wijzerWettelijk || '',
    })),
    [verlofaanvragenRaw]
  );

  const overuren = useMemo(() =>
    overurenRaw.map(o => ({
      ...o,
      Id: o.id, Status: o.status,
      MedewerkerNaam: o.medewerkerNaam,
      IngediendDoorNaam: o.ingediendDoorNaam,
      Datum: o.datum, Uren: o.uren,
      Omschrijving: o.omschrijving,
    })),
    [overurenRaw]
  );

  const feestdagen = useMemo(() =>
    feestdagenRaw.map(f => ({
      ...f, Id: f.id, Datum: f.datum, Naam: f.naam,
    })),
    [feestdagenRaw]
  );

  const vakantiePerioden = useMemo(() =>
    vakantiePeriodenRaw.map(vp => ({
      ...vp, Id: vp.id,
    })),
    [vakantiePeriodenRaw]
  );

  const roosterWijzigingen = useMemo(() =>
    roosterWijzigingenRaw.map(rw => ({
      ...rw, Id: rw.id,
    })),
    [roosterWijzigingenRaw]
  );

  const flexPlanning = useMemo(() => flexPlanningRaw, [flexPlanningRaw]);
  const flexUren = useMemo(() => flexUrenRaw, [flexUrenRaw]);
  const flexUitbetalingen = useMemo(() => flexUitbetalingenRaw, [flexUitbetalingenRaw]);

  // ---- Pending Outlook sync: verwerk mutaties van andere devices ----
  // Draait eenmalig na login wanneer Outlook verbonden is.
  // Vindt items zonder calendarEventId en synct ze alsnog.
  const pendingSyncDone = useRef(false);
  useEffect(() => {
    if (pendingSyncDone.current || loading || !currentUserRaw?.id) return;
    if (!currentUserRaw.isAdmin) return; // alleen admin heeft Outlook
    // Wacht even tot data geladen is en Outlook auto-reconnect klaar is
    const timer = setTimeout(async () => {
      if (!isOutlookConnected()) return;
      pendingSyncDone.current = true;
      const currentYear = new Date().getFullYear();
      let synced = 0;

      // Verlofaanvragen: goedgekeurd/ingediend dit jaar zonder calendarEventId
      const pendingVerlof = verlofaanvragenRaw.filter(v =>
        !v.calendarEventId &&
        v.jaar === currentYear &&
        (v.status === 'goedgekeurd' || v.status === 'ingediend' || v.isOpenZiek)
      );
      for (const v of pendingVerlof) {
        try {
          const vt = verlofTypesRaw.find(t => t.key === v.type);
          const mw = medewerkersRaw.find(m => m.id === v.medewerkerId);
          const kleur = medewerkerKleurMap?.[v.medewerkerId] || '#0078D4';
          const eventId = await createCalendarEvent(v, v.status, vt, mw, kleur);
          if (eventId) {
            await updateVerlofaanvraag(v.id, { calendarEventId: eventId });
            synced++;
          }
        } catch (err) { console.warn('Pending verlof sync mislukt:', err.message); }
        if (synced % 3 === 0) await new Promise(r => setTimeout(r, 300));
      }

      // Overuren: ingediend/goedgekeurd zonder calendarEventId
      const pendingOU = overurenRaw.filter(o =>
        !o.calendarEventId &&
        (o.status === 'ingediend' || o.status === 'goedgekeurd')
      );
      for (const o of pendingOU) {
        try {
          const eventId = await createOverurenEvent(o, medewerkerKleurMap);
          if (eventId) {
            await updateOveruren(o.id, { calendarEventId: eventId });
            synced++;
          }
        } catch (err) { console.warn('Pending overuren sync mislukt:', err.message); }
        if (synced % 3 === 0) await new Promise(r => setTimeout(r, 300));
      }

      // Flex planning: zonder calendarEventId
      const pendingFlex = flexPlanningRaw.filter(f => !f.calendarEventId);
      for (const f of pendingFlex) {
        try {
          const eventId = await createFlexPlanningEvent(f);
          if (eventId) {
            await updateFlexPlanning(f.id, { calendarEventId: eventId });
            synced++;
          }
        } catch (err) { console.warn('Pending flex sync mislukt:', err.message); }
        if (synced % 3 === 0) await new Promise(r => setTimeout(r, 300));
      }

      if (synced > 0) console.log(`✅ Pending Outlook sync: ${synced} items gesynct (${pendingVerlof.length} verlof, ${pendingOU.length} overuren, ${pendingFlex.length} flex)`);
    }, 8000); // 8 sec delay: wacht op data + Outlook auto-reconnect
    return () => clearTimeout(timer);
  }, [loading, currentUserRaw?.id, currentUserRaw?.isAdmin, verlofaanvragenRaw, overurenRaw, flexPlanningRaw, verlofTypesRaw, medewerkersRaw, medewerkerKleurMap]);

  // ---- Auth ----
  const login = async (email, wachtwoord) => {
    const found = medewerkersRaw.find(m => m.email === email);
    if (!found) return { ok: false, reden: 'ongeldig' };
    
    const match = await verifyWachtwoord(wachtwoord, found.wachtwoord);
    if (!match) return { ok: false, reden: 'ongeldig' };
    
    if (found.status === 'inactief') return { ok: false, reden: 'inactief' };
    if (found.status === 'uitdienst') return { ok: false, reden: 'uitdienst' };
    
    // Auto-migrate: hash plaintext wachtwoord bij eerste login
    if (!isGehasht(found.wachtwoord)) {
      try {
        const hashed = await hashWachtwoord(wachtwoord);
        await updateMedewerker(found.id, { password: hashed });
      } catch (err) { console.warn('Wachtwoord migratie mislukt:', err); }
    }

    // MFA check — alleen als MFA actief is én secret aanwezig
    if (found.mfaEnabled && found.mfaSecret) {
      return { ok: false, reden: 'mfa_required', mfaUser: found };
    }

    // Geen MFA ingesteld → direct inloggen
    setCurrentUserRaw(found);
    localStorage.setItem('slaaploods_session', JSON.stringify(found));
    return { ok: true, mustChangePassword: found.mustChangePassword === true };
  };

  // MFA verificatie bij login
  const verifyMFALogin = async (user, code) => {
    const { verifieerMFACode, verifieerBackupCode } = await import('../utils/mfa');
    
    // Probeer eerst als TOTP code
    if (verifieerMFACode(user.mfaSecret, code)) {
      setCurrentUserRaw(user);
      localStorage.setItem('slaaploods_session', JSON.stringify(user));
      return { ok: true, mustChangePassword: user.mustChangePassword === true };
    }
    
    // Probeer als backup code
    if (user.mfaBackupCodes && user.mfaBackupCodes.length > 0) {
      const result = verifieerBackupCode(code, user.mfaBackupCodes);
      if (result.valid) {
        // Update backup codes in Firestore (verwijder gebruikte code)
        await updateMedewerker(user.id, { mfaBackupCodes: result.remainingCodes });
        user.mfaBackupCodes = result.remainingCodes;
        setCurrentUserRaw(user);
        localStorage.setItem('slaaploods_session', JSON.stringify(user));
        return { ok: true, backupUsed: true, remaining: result.remainingCodes.length, mustChangePassword: user.mustChangePassword === true };
      }
    }
    
    return { ok: false, reden: 'mfa_ongeldig' };
  };

  // MFA setup voltooien
  const completeMFASetup = async (user, secret, backupCodes) => {
    await updateMedewerker(user.id, {
      mfaSecret: secret,
      mfaBackupCodes: backupCodes,
      mfaEnabled: true,
    });
    user.mfaSecret = secret;
    user.mfaBackupCodes = backupCodes;
    user.mfaEnabled = true;
    setCurrentUserRaw(user);
    localStorage.setItem('slaaploods_session', JSON.stringify(user));
    return { ok: true };
  };

  // MFA resetten (door admin)
  const resetMFA = async (userId) => {
    await updateMedewerker(userId, {
      mfaSecret: null,
      mfaBackupCodes: null,
      mfaEnabled: false,
    });
  };

  const logout = () => {
    setCurrentUserRaw(null);
    setActiveRol(null);
    setViewAsUserId(null);
    localStorage.removeItem('slaaploods_session');
    localStorage.removeItem('slaaploods_activeRol');
  };

  // ---- Werkrooster auto-sync helper (background, na verlof/ziek acties) ----
  const triggerWerkroosterSync = (updatedVerlof) => {
    if (!isOutlookConnected()) return;
    // Fire-and-forget in background, non-blocking
    (async () => {
      try {
        await autoSyncWerkrooster({
          medewerkers,
          verlofaanvragen: updatedVerlof || verlofaanvragenRaw,
          roosterWijzigingen,
          feestdagen,
          medewerkerKleurMap,
        });
      } catch (err) {
        console.warn('Auto-sync werkrooster mislukt:', err.message);
      }
    })();
  };

  // ---- Individuele event auto-sync helpers (fire-and-forget) ----
  const triggerVerlofEventSync = (verlofId, verlofData) => {
    if (!isOutlookConnected()) return;
    (async () => {
      try {
        const newEventId = await autoSyncVerlofEvent(verlofData, verlofTypes, medewerkers, medewerkerKleurMap);
        // Sla calendarEventId op in Firestore als deze is gewijzigd
        const oldEventId = verlofData.calendarEventId || null;
        if (newEventId !== oldEventId) {
          await updateVerlofaanvraag(verlofId, { calendarEventId: newEventId });
        }
      } catch (err) {
        console.warn('Auto-sync verlof-event mislukt:', err.message);
      }
    })();
  };

  const triggerOverurenEventSync = (ouId, ouData) => {
    if (!isOutlookConnected()) return;
    (async () => {
      try {
        const newEventId = await autoSyncOverurenEvent(ouData, medewerkerKleurMap);
        const oldEventId = ouData.calendarEventId || null;
        if (newEventId !== oldEventId) {
          await updateOveruren(ouId, { calendarEventId: newEventId });
        }
      } catch (err) {
        console.warn('Auto-sync overuren-event mislukt:', err.message);
      }
    })();
  };

  const triggerFlexEventSync = (planId, planData, action = 'upsert') => {
    if (!isOutlookConnected()) return;
    (async () => {
      try {
        const newEventId = await autoSyncFlexPlanningEvent(planData, action);
        if (action !== 'delete' && planId) {
          const oldEventId = planData.calendarEventId || null;
          if (newEventId !== oldEventId) {
            await updateFlexPlanning(planId, { calendarEventId: newEventId });
          }
        }
      } catch (err) {
        console.warn('Auto-sync flex-event mislukt:', err.message);
      }
    })();
  };

  // ---- Verlofaanvragen ----
  const indienVerlofaanvraag = async (data) => {
    // effectiveUser = viewAsUser (bij impersonation) of currentUser
    const effectief = viewAsUserRaw || currentUserRaw;
    const targetId = data.medewerkerId || effectief.id;
    const targetNaam = data.medewerkerNaam || effectief.naam;
    const isOpen = data.isOpenZiek ?? false;
    const gebruikOveruren = data.gebruikOveruren ?? false;
    const overurenGebruikt = data.overurenGebruikt ?? 0;
    const isVoorAnder = targetId !== currentUserRaw.id;
    const directGoedkeuren = (isVoorAnder && (data.directGoedkeuren ?? false)) || (data.forceGoedkeuren ?? false);
    const status = isOpen ? 'goedgekeurd' : (directGoedkeuren ? 'goedgekeurd' : 'ingediend');

    const verlofData = {
      medewerkerId: targetId,
      medewerkerNaam: targetNaam,
      ingediendDoor: currentUserRaw.id,
      ingediendDoorNaam: `${currentUserRaw.naam} (${rolNamenKort[effectiefRol] || effectiefRol})`,
      type: data.Type || data.type,
      startDatum: data.StartDatum || data.startDatum,
      eindDatum: data.EindDatum || data.eindDatum || '',
      beginTijd: data.StartTijd || data.beginTijd || null,
      eindTijd: data.EindTijd || data.eindTijd || null,
      uren: data.Uren || data.uren || 0,
      reden: data.Reden || data.reden || '',
      status,
      isOpenZiek: isOpen,
      gebruikOveruren,
      overurenGebruikt,
      jaar: new Date(data.StartDatum || data.startDatum).getFullYear(),
    };
    if (directGoedkeuren) {
      verlofData.goedgekeurdDoor = currentUserRaw.id;
      verlofData.goedgekeurdDoorNaam = `${currentUserRaw.naam} (${rolNamenKort[effectiefRol] || effectiefRol})`;
    }
    if (data.wijzerResultaat) {
      verlofData.wijzerResultaat = data.wijzerResultaat;
      verlofData.wijzerWettelijk = data.wijzerWettelijk || '';
    }

    const verlofId = await addVerlofaanvraag(verlofData);

    // Outlook event auto-sync (individueel verlof-event)
    if (status === 'goedgekeurd') {
      triggerVerlofEventSync(verlofId, { ...verlofData, id: verlofId });
    }

    // In-app notificatie
    const vt = verlofTypes.find(t => t.key === verlofData.type);
    const typeLabel = vt?.text || verlofData.type;
    const fmtD = (d) => d ? new Date(d + 'T00:00').toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' }) : '';
    const periodeStr = `${fmtD(verlofData.startDatum)}${verlofData.eindDatum ? ` t/m ${fmtD(verlofData.eindDatum)}` : ''} (${verlofData.uren}u)`;

    if (isOpen) {
      // Ziekmelding → notify admins/managers
      stuurNotificatie({
        ontvangerIds: getAdminManagerIds(),
        type: 'ziekmelding',
        titel: `Ziekmelding: ${targetNaam}`,
        bericht: `${targetNaam} is ziek gemeld per ${fmtD(verlofData.startDatum)}`,
        link: '/verlof-overzicht',
      });
    } else if (isVoorAnder) {
      // Ingediend namens iemand anders door manager/beheerder
      if (directGoedkeuren) {
        // Direct goedgekeurd → medewerker krijgt informatieve melding, nobody else
        stuurNotificatie({
          ontvangerIds: [targetId],
          type: 'verlof_goedgekeurd',
          titel: `${typeLabel} goedgekeurd`,
          bericht: `${currentUserRaw.naam} heeft ${typeLabel.toLowerCase()} voor je ingediend en goedgekeurd: ${periodeStr}`,
          link: '/verlof-overzicht',
        });
      } else {
        // Niet goedgekeurd → medewerker krijgt info
        stuurNotificatie({
          ontvangerIds: [targetId],
          type: 'verlof_ingediend',
          titel: `${typeLabel} ingediend`,
          bericht: `${currentUserRaw.naam} heeft ${typeLabel.toLowerCase()} voor je ingediend: ${periodeStr}. Status: wacht op goedkeuring.`,
          link: '/verlof-overzicht',
        });
        // Indiener zelf krijgt herinnering dat het nog goedgekeurd moet worden
        stuurNotificatie({
          ontvangerIds: [currentUserRaw.id],
          type: 'verlof_ingediend',
          titel: `⏳ Nog goedkeuren: ${targetNaam}`,
          bericht: `Je hebt ${typeLabel.toLowerCase()} ingediend voor ${targetNaam} (${periodeStr}) maar nog niet goedgekeurd.`,
          link: '/verlof-overzicht',
        });
      }
    } else {
      // Eigen aanvraag → notify admins/managers
      stuurNotificatie({
        ontvangerIds: getAdminManagerIds(),
        type: 'verlof_ingediend',
        titel: `Verlofaanvraag: ${targetNaam}`,
        bericht: `${targetNaam} vraagt ${typeLabel.toLowerCase()} aan: ${periodeStr}`,
        link: '/verlof-overzicht',
      });
    }

    // Outlook werkrooster auto-sync met de nieuwe verlofaanvraag meegenomen
    triggerWerkroosterSync([...verlofaanvragenRaw, verlofData]);
  };

  const updateVerlofStatus = async (id, status) => {
    await updateVerlofaanvraag(id, { status });

    // In-app notificatie naar de medewerker
    const verlofN = verlofaanvragenRaw.find(v => v.id === id);
    if (verlofN && verlofN.medewerkerId !== currentUserRaw?.id) {
      const statusLabel = status === 'goedgekeurd' ? 'goedgekeurd ✅' : status === 'afgekeurd' ? 'afgekeurd ❌' : 'ingetrokken';
      const notifType = status === 'goedgekeurd' ? 'verlof_goedgekeurd' : status === 'afgekeurd' ? 'verlof_afgekeurd' : 'verlof_ingetrokken';
      stuurNotificatie({
        ontvangerIds: [verlofN.medewerkerId],
        type: notifType,
        titel: `Verlof ${statusLabel}`,
        bericht: `Je verlofaanvraag (${verlofN.type}) is ${statusLabel} door ${currentUserRaw?.naam?.split(' ')[0]}`,
        link: '/verlof-overzicht',
      });
    }

    // Outlook werkrooster auto-sync met bijgewerkte status
    triggerWerkroosterSync(verlofaanvragenRaw.map(v => v.id === id ? { ...v, status } : v));

    // Outlook individueel verlof-event sync
    const verlofE = verlofaanvragenRaw.find(v => v.id === id);
    if (verlofE) triggerVerlofEventSync(id, { ...verlofE, status });
  };

  const wijzigVerlofaanvraag = async (id, updates) => {
    await updateVerlofaanvraag(id, updates);
    // Werkrooster auto-sync handelt Outlook bij
    triggerWerkroosterSync();
    // Individueel verlof-event sync
    const verlofE = verlofaanvragenRaw.find(v => v.id === id);
    if (verlofE) triggerVerlofEventSync(id, { ...verlofE, ...updates });
  };

  // Betermelden: sluit open ziekmelding af
  const betermeldVerlof = async (id, eindDatum, eindTijd, uren) => {
    await updateVerlofaanvraag(id, {
      eindDatum,
      eindTijd: eindTijd || null,
      uren,
      isOpenZiek: false,
    });

    // In-app notificatie: betermelding
    const ziekN = verlofaanvragenRaw.find(v => v.id === id);
    if (ziekN) {
      stuurNotificatie({
        ontvangerIds: getAdminManagerIds(),
        type: 'betermelding',
        titel: `Betermelding: ${ziekN.medewerkerNaam}`,
        bericht: `${ziekN.medewerkerNaam} is beter gemeld per ${eindDatum ? new Date(eindDatum + 'T00:00').toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' }) : 'vandaag'}`,
        link: '/verlof-overzicht',
      });
    }

    // Outlook werkrooster auto-sync met bijgewerkte betermelding
    triggerWerkroosterSync(verlofaanvragenRaw.map(v => v.id === id ? { ...v, eindDatum, isOpenZiek: false, uren } : v));

    // Individueel verlof-event sync (bijwerken met einddatum)
    const verlofE = verlofaanvragenRaw.find(v => v.id === id);
    if (verlofE) triggerVerlofEventSync(id, { ...verlofE, eindDatum, isOpenZiek: false, uren });
  };

  // ---- Overuren ----
  const berekenOverurenZaterdag = (startTijd, eindTijd) => {
    const [sh, sm] = startTijd.split(':').map(Number);
    const [eh, em] = eindTijd.split(':').map(Number);
    return (eh * 60 + em - sh * 60 - sm) / 60;
  };

  const berekenOverurenWerkdag = (startTijd, eindTijd) => {
    const [sh, sm] = startTijd.split(':').map(Number);
    const [eh, em] = eindTijd.split(':').map(Number);
    const startMin = sh * 60 + sm;
    const eindMin = eh * 60 + em;
    const pauzeStart = 12 * 60 + 30;
    const pauzeEind = 13 * 60;
    let totaalMin = eindMin - startMin;
    if (startMin < pauzeEind && eindMin > pauzeStart) {
      const overlapStart = Math.max(startMin, pauzeStart);
      const overlapEind = Math.min(eindMin, pauzeEind);
      if (overlapEind > overlapStart) totaalMin -= (overlapEind - overlapStart);
    }
    return totaalMin / 60;
  };

  const registreerOveruren = async (data) => {
    const datum = data.Datum || data.datum;
    const dag = new Date(datum + 'T12:00:00').getDay();
    let uren = data.Uren || data.uren;

    if (data.startTijd && data.eindTijd) {
      uren = dag === 6
        ? berekenOverurenZaterdag(data.startTijd, data.eindTijd)
        : berekenOverurenWerkdag(data.startTijd, data.eindTijd);
    }

    // effectiveUser = viewAsUser (bij impersonation) of currentUser
    // data.medewerkerId = expliciet gekozen medewerker (door manager/admin)
    const effectief = viewAsUserRaw || currentUserRaw;
    const targetId = data.medewerkerId || effectief.id;
    const targetNaam = data.medewerkerNaam || effectief.naam;

    const ouId = await addOveruren({
      medewerkerId: targetId,
      medewerkerNaam: targetNaam,
      ingediendDoor: currentUserRaw.id,
      ingediendDoorNaam: currentUserRaw.naam,
      datum,
      uren: Math.round(uren * 100) / 100,
      omschrijving: data.Omschrijving || data.omschrijving || '',
      startTijd: data.startTijd || null,
      eindTijd: data.eindTijd || null,
      isZaterdag: dag === 6,
      status: 'ingediend',
    });

    // Outlook sync: direct zichtbaar in agenda bij indienen
    triggerOverurenEventSync(ouId, {
      medewerkerId: targetId,
      medewerkerNaam: targetNaam,
      datum,
      uren: Math.round(uren * 100) / 100,
      omschrijving: data.Omschrijving || data.omschrijving || '',
      startTijd: data.startTijd || null,
      eindTijd: data.eindTijd || null,
      status: 'ingediend',
    });

    // Notificatie
    if (targetId === currentUserRaw.id) {
      // Eigen overuren → notify admins
      stuurNotificatie({
        ontvangerIds: getAdminManagerIds(),
        type: 'overuren_ingediend',
        titel: `Overuren: ${targetNaam}`,
        bericht: `${targetNaam} heeft ${Math.round(uren * 100) / 100} overuren geregistreerd op ${new Date(datum + 'T00:00').toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' })}`,
        link: '/',
      });
    } else {
      // Namens medewerker → notify medewerker
      stuurNotificatie({
        ontvangerIds: [targetId],
        type: 'overuren_ingediend',
        titel: `Overuren geregistreerd`,
        bericht: `${currentUserRaw.naam} heeft ${Math.round(uren * 100) / 100} overuren voor je geregistreerd op ${new Date(datum + 'T00:00').toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' })}`,
        link: '/',
      });
    }
  };

  const updateOverurenStatus = async (id, status) => {
    await updateOveruren(id, { status });

    // Notificatie naar medewerker
    const ou = overurenRaw.find(o => o.id === id);
    if (ou && ou.medewerkerId !== currentUserRaw?.id) {
      const statusLabel = status === 'goedgekeurd' ? 'goedgekeurd ✅' : status === 'uitbetaald' ? 'uitbetaald 💰' : 'afgekeurd ❌';
      stuurNotificatie({
        ontvangerIds: [ou.medewerkerId],
        type: status === 'goedgekeurd' ? 'overuren_goedgekeurd' : status === 'uitbetaald' ? 'overuren_uitbetaald' : 'overuren_afgekeurd',
        titel: `Overuren ${statusLabel}`,
        bericht: `Je overurenregistratie (${ou.uren}u) is ${statusLabel}`,
        link: '/',
      });
    }

    // Check zaterdag drempel doorbraak bij goedkeuring
    if (status === 'goedgekeurd' && ou?.isZaterdag) {
      const mw = medewerkersRaw.find(m => m.id === ou.medewerkerId);
      const drempel = mw?.zaterdagDrempel || 0;
      if (drempel > 0) {
        const todayStr = new Date().toISOString().split('T')[0];
        const zaUrenVoor = overurenRaw
          .filter(o => o.medewerkerId === ou.medewerkerId && o.status === 'goedgekeurd' && o.isZaterdag && o.id !== id && (o.datum || '') <= todayStr)
          .reduce((s, o) => s + (o.uren || 0), 0);
        const zaUrenNa = zaUrenVoor + (ou.uren || 0);
        // Drempel net overschreden met deze goedkeuring?
        if (zaUrenVoor < drempel && zaUrenNa >= drempel) {
          const overurenBoven = Math.round((zaUrenNa - drempel) * 100) / 100;
          // Notificatie naar medewerker + admins/managers
          stuurNotificatie({
            ontvangerIds: [ou.medewerkerId, ...getAdminManagerIds()],
            type: 'zaterdag_drempel',
            titel: `📅 Zaterdaguren drempel bereikt: ${mw.naam || ''}`,
            bericht: `${mw.naam || 'Medewerker'} heeft de ${drempel}u onbetaalde zaterdaguren drempel bereikt. Vanaf nu tellen zaterdaguren als overuren (+${overurenBoven}u).`,
            link: '/overuren',
          });
        }
      }
    }

    // Outlook overuren-event sync (alle statuswijzigingen)
    if (ou) {
      triggerOverurenEventSync(id, { ...ou, status });
    }
  };

  // ---- Medewerker CRUD (admin) ----
  const voegMedewerkerToe = async (data) => {
    const hashedWw = data.wachtwoord ? await hashWachtwoord(data.wachtwoord) : '';
    await addMedewerker({
      naam: data.naam,
      email: data.email,
      wachtwoord: hashedWw,
      rol: data.rol || 'medewerker',
      isManager: data.isManager ?? false,
      isAdmin: data.isAdmin ?? false,
      status: 'actief',
      afdeling: data.afdeling || '',
      contractUren: data.contractUren || 40,
      indienstDatum: data.indienstDatum || new Date().toISOString().split('T')[0],
      contractWijzigingen: [],
      meegenomenUren: 0,
      meegenomenUrenJaar: null,
      flexEnabled: data.flexEnabled ?? false,
      werkrooster: data.werkrooster || null,
      telefoon: data.telefoon || '',
      pendingApproval: data.pendingApproval ?? false,
      kleur: data.kleur || '#E86E1C',
    });
  };

  const werkMedewerkerBij = async (id, updates) => {
    // Hash wachtwoord als het wordt gewijzigd
    if (updates.password && !isGehasht(updates.password)) {
      updates.password = await hashWachtwoord(updates.password);
    }
    if (updates.wachtwoord && !isGehasht(updates.wachtwoord)) {
      updates.wachtwoord = await hashWachtwoord(updates.wachtwoord);
    }
    await updateMedewerker(id, updates);
  };

  // Wachtwoord wijzigen (door gebruiker zelf of admin)
  const wijzigWachtwoord = async (medewerkerId, huidigWachtwoord, nieuwWachtwoord) => {
    const mw = medewerkersRaw.find(m => m.id === medewerkerId);
    if (!mw) throw new Error('Medewerker niet gevonden');

    // Als het niet een admin-reset is, verifieer huidig wachtwoord
    if (huidigWachtwoord !== null) {
      const match = await verifyWachtwoord(huidigWachtwoord, mw.wachtwoord || mw.password);
      if (!match) throw new Error('Huidig wachtwoord is onjuist');
    }

    const hashed = await hashWachtwoord(nieuwWachtwoord);
    await updateMedewerker(medewerkerId, { password: hashed, wachtwoord: hashed, mustChangePassword: false });
    
    // Update sessie als het eigen wachtwoord is
    if (medewerkerId === currentUserRaw?.id) {
      const session = { ...currentUserRaw, wachtwoord: hashed, mustChangePassword: false };
      localStorage.setItem('slaaploods_session', JSON.stringify(session));
      setCurrentUserRaw(prev => prev ? { ...prev, mustChangePassword: false } : prev);
    }
    return true;
  };

  const verwijderMedewerker = async (id) => {
    await deleteMedewerker(id);
  };

  const verwijderMedewerkerCompleet = async (id) => {
    // Verwijder ook Outlook events voor deze medewerker
    if (isOutlookConnected()) {
      try {
        const mwVerlof = verlofaanvragenRaw.filter(v => v.medewerkerId === id && v.calendarEventId);
        for (const v of mwVerlof) {
          await deleteCalendarEvent(v.calendarEventId);
        }
        const mwOveruren = overurenRaw.filter(o => o.medewerkerId === id && o.calendarEventId);
        for (const o of mwOveruren) {
          await deleteCalendarEvent(o.calendarEventId);
        }
        const mwFlex = flexPlanningRaw.filter(p => p.flexkrachtId === id && p.calendarEventId);
        for (const p of mwFlex) {
          await deleteCalendarEvent(p.calendarEventId);
        }
      } catch { /* niet kritiek */ }
    }
    await deleteMedewerkerCompleet(id);
  };

  /**
   * Reset alle mutaties van een medewerker.
   * Verwijdert verlof, overuren, ziek, flex, roosterwijzigingen, archief, notificaties.
   * Behoudt het medewerker profiel (naam, rooster, contracturen etc.)
   */
  const resetMedewerkerData = async (id) => {
    // Eerst Outlook events verwijderen
    if (isOutlookConnected()) {
      try {
        const mwVerlof = verlofaanvragenRaw.filter(v => v.medewerkerId === id && v.calendarEventId);
        for (const v of mwVerlof) {
          await deleteCalendarEvent(v.calendarEventId);
        }
        const mwOveruren = overurenRaw.filter(o => o.medewerkerId === id && o.calendarEventId);
        for (const o of mwOveruren) {
          await deleteCalendarEvent(o.calendarEventId);
        }
        const mwFlex = flexPlanningRaw.filter(p => p.flexkrachtId === id && p.calendarEventId);
        for (const p of mwFlex) {
          await deleteCalendarEvent(p.calendarEventId);
        }
      } catch { /* niet kritiek */ }
    }
    // Dan alle Firestore mutaties verwijderen
    const stats = await resetMedewerkerMutaties(id);
    return stats;
  };

  // ---- Feestdagen (admin) ----
  const voegFeestdagToe = async (data) => {
    await addFeestdag({ datum: data.datum, naam: data.naam });
  };

  const verwijderFeestdag = async (id) => {
    await deleteFeestdag(id);
  };

  // ---- Vakantieperioden (admin) ----
  const voegVakantiePeriodeToe = async (data) => {
    await addVakantiePeriode({ naam: data.naam, startDatum: data.startDatum, eindDatum: data.eindDatum });
  };

  const werkVakantiePeriodeBij = async (id, updates) => {
    await updateVakantiePeriode(id, updates);
  };

  const verwijderVakantiePeriodeFn = async (id) => {
    await deleteVakantiePeriode(id);
  };

  // ---- Roosterwijzigingen (admin/manager) ----
  const voegRoosterWijzigingToe = async (data) => {
    await addRoosterWijziging({
      medewerkerId: data.medewerkerId,
      medewerkerNaam: data.medewerkerNaam,
      datum: data.datum,
      startTijd: data.startTijd || null,
      eindTijd: data.eindTijd || null,
      heleDag: data.heleDag ?? true,
      omschrijving: data.omschrijving || '',
    });
  };

  const verwijderRoosterWijzigingFn = async (id) => {
    await deleteRoosterWijziging(id);
  };

  // ---- Flex Planning (manager/admin) ----
  const planFlexkracht = async (data) => {
    const planStatus = data.planStatus || 'pending';
    const planId = await addFlexPlanning({
      flexkrachtId: data.flexkrachtId,
      flexkrachtNaam: data.flexkrachtNaam,
      datum: data.datum,
      startTijd: data.startTijd,
      eindTijd: data.eindTijd,
      uren: data.uren,
      omschrijving: data.omschrijving || '',
      ingeplandDoor: currentUserRaw.id,
      ingeplandDoorNaam: currentUserRaw.naam,
      planStatus,
    });

    // In-app notificatie naar flexkracht (altijd sturen als ze app-toegang hebben)
    stuurNotificatie({
      ontvangerIds: [data.flexkrachtId],
      type: 'flex_ingepland',
      titel: planStatus === 'goedgekeurd' ? 'Planning bevestigd' : 'Ingepland',
      bericht: `Je bent ingepland op ${new Date(data.datum + 'T00:00').toLocaleDateString('nl-NL', { weekday: 'short', day: 'numeric', month: 'short' })} ${data.startTijd}–${data.eindTijd} (${data.uren}u)${planStatus === 'pending' ? ' — wacht op bevestiging' : ''}`,
      link: '/flex',
    });

    // Outlook flex-event sync
    triggerFlexEventSync(planId, { ...data, id: planId, planStatus });
  };

  const verwijderFlexPlanning = async (id) => {
    // Outlook event verwijderen voor deze planning
    const plan = flexPlanningRaw.find(p => p.id === id);
    if (plan) triggerFlexEventSync(id, plan, 'delete');
    await deleteFlexPlanning(id);
  };

  // ---- Flex Uren (flexkracht) ----
  const registreerFlexUren = async (data) => {
    await addFlexUren({
      flexkrachtId: data.flexkrachtId || currentUserRaw.id,
      flexkrachtNaam: data.flexkrachtNaam || currentUserRaw.naam,
      datum: data.datum,
      startTijd: data.startTijd,
      eindTijd: data.eindTijd || null,
      uren: data.uren || 0,
      omschrijving: data.omschrijving || '',
      status: data.status || 'ingediend',
      isOpenKlok: !data.eindTijd, // true als alleen ingeklokt
    });
  };

  const wijzigFlexUren = async (id, updates) => {
    await updateFlexUren(id, updates);
  };

  const wijzigFlexPlanning = async (id, updates) => {
    await updateFlexPlanning(id, updates);
    // Outlook flex-event sync (bijwerken)
    const plan = flexPlanningRaw.find(p => p.id === id);
    if (plan) triggerFlexEventSync(id, { ...plan, ...updates });
  };

  // ---- Flex Uitbetalingen (admin) ----
  const betaalFlexUren = async (data) => {
    await addFlexUitbetaling({
      flexkrachtId: data.flexkrachtId,
      flexkrachtNaam: data.flexkrachtNaam,
      datum: new Date().toISOString().split('T')[0],
      uren: data.uren,
      omschrijving: data.omschrijving || 'Uitbetaling flex uren',
      type: data.type || 'uitbetaling',
    });
  };

  // ---- Jaarafsluiting (admin) ----
  const voerJaarafsluitingUit = async (jaar) => {
    for (const m of medewerkersRaw) {
      if (m.rol === 'flexkracht') continue;
      const contractUren = m.contractUren || 0;
      const berekendeUren = berekenVerlofUrenProRata(
        contractUren, m.indienstDatum, m.contractWijzigingen, jaar
      );
      const meegenomenUren = m.meegenomenUren || 0;
      const opgenomenUren = verlofaanvragenRaw
        .filter(v => v.medewerkerId === m.id && v.status === 'goedgekeurd' && v.jaar === jaar && trektAfVanVerlof(v.type))
        .reduce((sum, v) => sum + (v.uren || 0), 0);
      const overurenTotaal = overurenRaw
        .filter(o => o.medewerkerId === m.id && o.status === 'goedgekeurd')
        .reduce((sum, o) => sum + (o.uren || 0), 0);
      const eindsaldo = berekendeUren + meegenomenUren - opgenomenUren;

      await addJaararchief({
        medewerkerId: m.id,
        medewerkerNaam: m.naam,
        jaar,
        contractUren,
        berekendeUren,
        meegenomenUren,
        opgenomenUren,
        overurenSaldo: overurenTotaal,
        eindsaldo,
      });

      // Meegenomen uren NIET automatisch zetten — beheerder doet dit handmatig
    }
  };

  // ---- Verloftypen CRUD ----
  const voegVerlofTypeToe = async (data) => await addVerlofType(data);
  const werkVerlofTypeBij = async (id, updates) => await updateVerlofType(id, updates);
  const verwijderVerlofTypeFn = async (id) => await deleteVerlofType(id);

  const value = {
    currentUser: mappedCurrentUser, realUser,
    isAdmin, isManager, isFlexkracht, isMedewerker, canManage,
    beschikbareRollen, effectiefRol, switchRol,
    profielLabel,
    viewAsUserId, startViewAs, stopViewAs, isViewingAs: !!viewAsUserRaw,
    medewerkers, verlofaanvragen, overuren, feestdagen, vakantiePerioden, roosterWijzigingen,
    medewerkerKleurMap,
    jaararchief, flexPlanning, flexUren, flexUitbetalingen,
    verlofTypes, trektAfVanVerlof, VERLOF_FACTOR,
    saldo, flexSaldo, loading,
    login, logout, wijzigWachtwoord, verifyMFALogin, completeMFASetup, resetMFA,
    indienVerlofaanvraag, updateVerlofStatus, wijzigVerlofaanvraag, betermeldVerlof,
    registreerOveruren, updateOverurenStatus, updateOveruren,
    berekenOverurenZaterdag, berekenOverurenWerkdag,
    voegMedewerkerToe, werkMedewerkerBij, verwijderMedewerker, verwijderMedewerkerCompleet, resetMedewerkerData,
    voegFeestdagToe, verwijderFeestdag,
    voegVakantiePeriodeToe, werkVakantiePeriodeBij, verwijderVakantiePeriode: verwijderVakantiePeriodeFn,
    voegRoosterWijzigingToe, verwijderRoosterWijziging: verwijderRoosterWijzigingFn,
    planFlexkracht, verwijderFlexPlanning, wijzigFlexPlanning,
    registreerFlexUren, wijzigFlexUren, betaalFlexUren,
    voerJaarafsluitingUit,
    voegVerlofTypeToe, werkVerlofTypeBij, verwijderVerlofType: verwijderVerlofTypeFn,
    menuConfig: menuConfigRaw, saveMenuConfig,
    notificaties, ongelezen, markeerGelezen: markeerGelezenFn, markeerAllesGelezen: markeerAllesGelezenFn,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp() {
  return useContext(AppContext);
}
